import { useState } from "react";
import SalesRevenue from "@/pages/modules/SalesRevenue";
import PurchasesPayables from "@/pages/modules/PurchasesPayables";
import Inventory from "@/pages/modules/Inventory";
import Expenses from "@/pages/modules/Expenses";
import EvidencePanel from "@/pages/modules/EvidencePanel";
import SubstantiveDashboard from "@/pages/modules/substantive/SubstantiveDashboard";
import { DOMAINS, domainFor } from "@/pages/modules/substantive/domains";

/**
 * Pengujian Substantif — one workspace over four domains that were four peer tabs.
 *
 * This is navigation and orchestration. The domain components below are the same ones that were
 * mounted before, rendered with the same props and the same evidence panel: sales still does
 * revenue recognition, cut-off, AR and VAT output; purchases still does unrecorded liabilities,
 * WHT and subsequent payment; inventory still does NRV, expiry, count and costing; expenses
 * still does classification, DPP and unusual journals. Roughly 2,750 lines of domain logic and
 * 950 lines of their tests are untouched.
 *
 * Consolidating the shell must not flatten what the domains do. Where they differ — and they
 * differ a great deal — the difference is preserved rather than averaged into a generic screen.
 */

const COMPONENTS = {
  sales: SalesRevenue,
  purchases: PurchasesPayables,
  inventory: Inventory,
  expenses: Expenses,
};

/** The audit lifecycle every domain moves through, shown as orientation, not as a wizard. */
const LIFECYCLE = [
  'Populasi', 'Rekonsiliasi', 'Risiko & Asersi', 'Materialitas', 'Sampling',
  'Prosedur', 'Bukti', 'Eksepsi', 'AJE / RJE', 'Kesimpulan', 'Review',
];

export default function SubstantiveCenter({ id, initialDomain = null, onDomainChange = null }) {
  // A legacy ?tab=sales link names a domain; without one the dashboard is the landing view.
  const [view, setView] = useState(initialDomain || 'dashboard');

  // Each domain stays individually addressable. Before consolidation ?tab=inventory was a link
  // you could send someone; it still is, and it still means the same screen. The parent owns the
  // URL, so it is told which domain is open and keeps local state as the fallback for a
  // standalone mount.
  const select = (next) => {
    setView(next);
    if (onDomainChange) onDomainChange(next === 'dashboard' ? null : next);
  };

  // An unrecognised domain resolves to nothing, and nothing means the dashboard rather than a
  // blank screen or a crash on a key that is not in the registry.
  const active = view === 'dashboard' ? null : domainFor(view);
  const Domain = active ? COMPONENTS[active.key] : null;

  return (
    <div data-testid="substantive-center">
      <nav className="mb-4 flex flex-wrap gap-1 border-b pb-2" aria-label="Domain pengujian substantif">
        <button
          type="button"
          onClick={() => select('dashboard')}
          data-testid="substantive-nav-dashboard"
          aria-current={!active ? 'page' : undefined}
          className={`text-sm rounded px-3 py-1.5 border ${
            !active ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
          }`}
        >
          Dashboard
        </button>
        {DOMAINS.map((domain) => (
          <button
            key={domain.key}
            type="button"
            onClick={() => select(domain.key)}
            data-testid={`substantive-nav-${domain.key}`}
            aria-current={view === domain.key ? 'page' : undefined}
            className={`text-sm rounded px-3 py-1.5 border ${
              view === domain.key ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
            }`}
          >
            {domain.label}
          </button>
        ))}
      </nav>

      {!active ? (
        <SubstantiveDashboard id={id} onOpenDomain={select} />
      ) : (
        <div data-testid={`substantive-domain-${active.key}`}>
          <div className="mb-3">
            <h3 className="font-head font-semibold">{active.label}</h3>
            {/* The sequence the work runs in, as orientation. It carries no state and marks
                nothing complete: what has actually been done is in the domain below. */}
            <ol className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground"
                aria-label="Urutan pengujian substantif (urutan kerja, bukan status penyelesaian)">
              {LIFECYCLE.map((step, index) => (
                <li key={step}>
                  {step}{index < LIFECYCLE.length - 1 && <span className="ml-2">→</span>}
                </li>
              ))}
            </ol>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Urutan kerja, bukan status penyelesaian.
            </p>
          </div>
          {/* The existing module, unchanged, with the evidence panel it already had. */}
          <Domain id={id} />
          <EvidencePanel id={id} phase={active.evidencePhase} module={active.evidenceModule} />
        </div>
      )}
    </div>
  );
}
